import { FileTypeBadge } from './FileTypeBadge';
import { usePluginMenuItems } from './usePluginMenuItems';
/* =====================================================================
 * MessageInput — 메시지 입력 + 파일 첨부 (ChatGPT 스타일)
 * ===================================================================== */

import React, { useRef, useState, useCallback, useEffect, useMemo, Suspense } from 'react';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { ArrowUp, Plus, X, Square, Loader, Maximize2, Minimize2, Upload, Clipboard, BookOpenText, BookOpen, Sparkles, HardDrive, FolderOpen, SlidersHorizontal } from 'lucide-react';
import { v4 as uuid } from 'uuid';
import { useChatStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { WORKSPACE_MODES, type FileAttachment, type WorkspaceMode } from '@auw/workspace-utils';
import {
  buildHitlAnswer,
  renderHitlAnswerText,
  type HitlSelection,
} from '@auw/workspace-utils';
import { HitlQuestionCard } from '../HitlQuestionCard';
import { useFeatureFlags } from '@auw/feature-flags';
import { Tooltip } from '@auw/workspace-ui';
import { DropdownMenu, type DropdownMenuItem } from '@auw/workspace-ui';
import { Modal } from '@auw/workspace-ui';
import { canPreviewFile } from '@auw/workspace-ui';
import {
  buildAttachmentCountLimitToast,
  buildAttachmentSizeLimitToast,
  buildAttachmentTypeLimitToast,
  validateAttachmentBatch,
} from '../attachmentConstraints';
import { useComposerTarget } from '@auw/workspace-features';
import { extractClipboardFiles } from '@auw/workspace-utils';
import { useReboModeStore, getReboModeOptions, type ReboMode } from '@auw/stores';
import { useChatModelStore, getChatModelOptions } from '@auw/stores';
import { useSkillStore } from '@auw/stores';
import { useNavigate } from 'react-router-dom';
import { modeToPath } from '@auw/workspace-utils';
import { useOfficialSkillStore } from '@auw/stores';
import { useChatFeature } from '../ChatModeContext';
import SkillModal from '../SkillModal';
import { SystemPromptModal } from '../SystemPromptModal';
import { useSystemPromptStore, getSelectedSystemPromptId, isSystemPromptMode } from '@auw/stores';
import { BrandChip } from '@auw/workspace-ui';
import { resolveLocalized } from '@auw/workspace-utils';
import { emitAppEvent, onAppEvent } from '@auw/stores';
import { useTranslation } from 'react-i18next';
import { McpApprovalSelect } from './McpApprovalSelect';
import { ComposerSelect } from './ComposerSelect';
import { COMPOSER_SLOT_ATTR, CompactComposerSettings, useComposerTier } from './CompactComposerSettings';
import { WorkspaceFilesModal } from '../../storage/WorkspaceFilesModal';
import { getWikiSourcePickerComponent } from '../../../lib/wikiSourcePickerSlot';
import {
  adoptDraftProjectWiki,
  bindProjectWiki,
  projectWikiRequestMetadata,
  resolveProjectWikiBinding,
  useProjectWikiDraft,
  type ProjectWikiBinding,
} from '../../../lib/projectWikiBinding';

function cloneAttachmentsForSubmit(attachments: FileAttachment[]): FileAttachment[] {
  return attachments.map((attachment) => ({
    ...attachment,
    previewUrl:
      attachment.previewUrl?.startsWith('blob:') && attachment.file
        ? URL.createObjectURL(attachment.file)
        : attachment.previewUrl,
  }));
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrapper = styled.div`
  width: 100%;
  min-width: 0;
  max-width: ${({ theme }) => theme.sizes.maxInputWidth};
  margin: 0 auto;
  padding: ${({ theme }) => theme.input.wrapperPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.wrapperPad};
  }
`;

const InputCard = styled.div<{ $focus: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(40 40 42 / calc(0.85 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.85 * var(--message-input-surface-opacity, 1)))'};
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 2px 12px rgba(0,0,0,0.25)'
      : '0 2px 12px rgba(0,0,0,0.05)'};
  transition: all 0.2s ease;
  ${({ $focus, theme }) =>
    $focus &&
    css`
      border-color: ${theme.mode === 'dark'
        ? 'rgba(255,255,255,0.14)'
        : 'rgba(0,0,0,0.1)'};
      box-shadow: ${theme.mode === 'dark'
        ? '0 4px 20px rgba(0,0,0,0.35), 0 0 0 0.5px rgba(255,255,255,0.08)'
        : '0 4px 20px rgba(0,0,0,0.08), 0 0 0 0.5px rgba(0,0,0,0.04)'};
    `}
`;

/* File previews row */
const Attachments = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.input.attachGap};
  padding: ${({ theme }) => theme.input.attachPad};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: ${({ theme }) => theme.inputMobile.attachGap};
    padding: ${({ theme }) => theme.inputMobile.attachPad};
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

/* ---- 컨텍스트 칩 행 ----
 *
 * 시스템 프롬프트 · 프로젝트 위키 · 스킬 칩(공통 `BrandChip`)이 모두 이 한 행에
 * 앉는다. 첨부 파일 카드와 같은 줄에는 두지 않는다 — 첨부는 이번 턴에만 실리는
 * 것이고 이 칩들은 대화(또는 모드) 전체에 걸린 범위라 성질이 다르다. 같이 두면
 * 사용자가 X 를 눌러 이번 턴만 빼려 했는데 대화에서 위키가 떨어진다. 그래서
 * 첨부 행 *위* 에 따로 둔다.
 *
 * 좁은 화면: 칩이 행 폭을 넘으면 다음 줄로 감싸고(flex-wrap), 칩 하나가 행보다
 * 길면 `min-width: 0; max-width: 100%` 로 눌러 라벨이 말줄임된다. 그래야 긴
 * 스킬/위키 이름이 입력창 카드를 가로로 밀어내지 않는다. */
const ContextChipRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  padding: ${({ theme }) => theme.input.attachPad};
  padding-bottom: 0;

  > * {
    min-width: 0;
    max-width: 100%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 4px;
    padding: ${({ theme }) => theme.inputMobile.attachPad};
    padding-bottom: 0;
  }
`;

/* ---- File card ---- */

const FileCard = styled.div<{ $previewable?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.04 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.8 * var(--message-input-surface-opacity, 1)))'};
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  border-radius: 12px;
  padding: ${({ theme }) => theme.input.filePad};
  ${({ $previewable }) => $previewable && 'padding-right: 52px;'}
  min-width: 180px;
  max-width: 240px;
  animation: ${fadeIn} 0.2s ease;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.2)'
      : '0 1px 4px rgba(0,0,0,0.04)'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.3)'
        : '0 2px 8px rgba(0,0,0,0.06)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.filePad};
    ${({ $previewable }) => $previewable && 'padding-right: 48px;'}
    min-width: 160px;
    max-width: 200px;
  }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FileName = styled.span`
  font-size: ${({ theme }) => theme.input.fileFontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.fileFontSize};
  }
`;

const FileSize = styled.span`
  font-size: ${({ theme }) => theme.input.fileSizeFontSize};
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.2;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.fileSizeFontSize};
  }
`;

const ImageThumb = styled.img`
  width: ${({ theme }) => theme.input.fileIconSize};
  height: ${({ theme }) => theme.input.fileIconSize};
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.fileIconSize};
    height: ${({ theme }) => theme.inputMobile.fileIconSize};
  }
`;

/* ---- File card action buttons ---- */

const CardActions = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  gap: 1px;
`;

const CardActionBtn = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* Input row */
const InputRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding: ${({ theme }) => theme.input.rowPad};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.inputMobile.rowPad};
  }
`;

const ComposerInputWrap = styled.div`
  width: 100%;
  position: relative;
  padding: 8px 16px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 6px 12px 0;
  }
`;

const EditorInput = styled.div`
  width: 100%;
  min-height: 24px;
  max-height: ${({ theme }) => theme.sizes.inputMaxHeight};
  overflow-y: hidden;
  padding: ${({ theme }) => theme.input.textPad};
  padding-left: 8px;
  padding-right: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.input.fontSize};
  line-height: ${({ theme }) => theme.input.lineHeight};
  white-space: pre-wrap;
  word-break: break-word;
  outline: none;
  /*
   * caret 가시성 — host 앱의 가장 핵심 UI 요소라 !important 로 보호.
   *
   * 사고 배경: contentEditable 의 caret-color 는 명시 안 되면 browser 가
   * text color 를 따라가는 게 보통이지만, 외부 stylesheet (예: vendor
   * docxEditor 의 editor-base.css 가 깔던 [contenteditable='true'] {
   * caret-color: #000 } 글로벌 룰 — 별도 패치로 .ep-root 안으로 좁힘) 와
   * specificity 가 동등해 cascade 순서로 외부 룰이 이기는 케이스가 발생
   * 했다. 다크 모드에서 캐럿이 검정으로 박혀 안 보이는 회귀가 보고됐다.
   *
   * 채팅 input 의 caret 은 사용자 시야와 입력 위치 정체성에 1:1 이라,
   * 어떤 vendor / 미래 leak 로부터도 보호되어야 한다. !important 가
   * 일반적으로 비호선이지만 이 자리에서는 정당화된다 — 같은 선택자에서
   * 이걸 의도적으로 덮어쓸 호출자가 없다.
   *
   * 자식 요소 (mention chip 등) 안에서 caret 이 떠 있을 때도 같은 색이
   * 되도록 명시 — caret-color 는 spec 상 inherit 되지만 일부 브라우저
   * 동작 차이를 막는 안전망.
   */
  caret-color: ${({ theme }) => theme.colors.textPrimary} !important;
  background: transparent;

  & * {
    caret-color: inherit;
  }

  &[data-empty='true'] {
    min-height: ${({ theme }) => theme.input.iconBtnSize};
  }

  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
  }
  &[data-at-max='true']::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'};
  }
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  &[data-at-max='true'] {
    scrollbar-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} transparent;
  }

  [data-mention-chip='true'] {
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
    padding: 0 6px;
    margin: 0 1px;
    font-size: 0.86em;
    line-height: 1.35;
    font-weight: 600;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#7dd3fc' : '#1264a3')};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(56,189,248,0.18)' : 'rgba(18,100,163,0.14)'};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 22px;
    padding: ${({ theme }) => theme.inputMobile.textPad};
    padding-left: 6px;
    padding-right: 6px;
    font-size: ${({ theme }) => theme.inputMobile.fontSize};
    line-height: ${({ theme }) => theme.inputMobile.lineHeight};
  }
`;

const ComposerPlaceholder = styled.div`
  position: absolute;
  top: 12px;
  left: 16px;
  right: 16px;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: ${({ theme }) => theme.input.textPad};
  padding-left: 8px;
  padding-right: 8px;
  font-size: ${({ theme }) => theme.input.fontSize};
  line-height: ${({ theme }) => theme.input.lineHeight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 10px;
    left: 12px;
    right: 12px;
    padding: ${({ theme }) => theme.inputMobile.textPad};
    padding-left: 6px;
    padding-right: 6px;
    font-size: ${({ theme }) => theme.inputMobile.fontSize};
    line-height: ${({ theme }) => theme.inputMobile.lineHeight};
  }
`;

const MentionDropdown = styled.div`
  position: fixed;
  transform: translateY(-100%);
  z-index: 80;
  min-width: 220px;
  max-width: 320px;
  max-height: 220px;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(40 40 42 / calc(0.92 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.92 * var(--message-input-surface-opacity, 1)))'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'};
  padding: 6px;
`;

const MentionSuggestionRow = styled.div<{ $focused: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${({ $focused, theme }) =>
    $focused
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)')
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
  transition: background 0.15s ease;
`;

const MentionSuggestionType = styled.span`
  flex-shrink: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 2px 6px;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.bgHover};
`;

const MentionSuggestionLabel = styled.span`
  min-width: 0;
  font-size: 0.82rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconBtn = styled.button<{ $primary?: boolean; $disabled?: boolean }>`
  width: ${({ theme }) => theme.input.iconBtnSize};
  height: ${({ theme }) => theme.input.iconBtnSize};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $primary, theme }) =>
    $primary
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
          &:hover:not(:disabled) {
            opacity: 0.85;
          }
        `
      : css`
          color: ${theme.colors.textMuted};
          &:hover {
            background: ${theme.colors.bgHover};
            color: ${theme.colors.textPrimary};
          }
        `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.3;
      cursor: not-allowed;
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.iconBtnSize};
    height: ${({ theme }) => theme.inputMobile.iconBtnSize};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const OuterWrapper = styled.div<{ $surfaceOpacity: number }>`
  position: relative;
  --message-input-surface-opacity: ${({ $surfaceOpacity }) => $surfaceOpacity};
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const UploadingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 0;
  font-size: ${({ theme }) => theme.input.uploadFontSize};
  color: ${({ theme }) => theme.colors.textMuted};

  svg {
    animation: ${spin} 1s linear infinite;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.uploadFontSize};
  }
`;

const SpinIcon = styled.span`
  display: flex;
  animation: ${spin} 1s linear infinite;
`;

/* ---- Pasted block card ---- */

const PastedCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  max-width: 240px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'};
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(255 255 255 / calc(0.04 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.8 * var(--message-input-surface-opacity, 1)))'};
  overflow: hidden;
  cursor: pointer;
  animation: ${fadeIn} 0.2s ease;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 4px rgba(0,0,0,0.2)'
      : '0 1px 4px rgba(0,0,0,0.04)'};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 2px 8px rgba(0,0,0,0.3)'
        : '0 2px 8px rgba(0,0,0,0.06)'};
  }
`;

const PastedPreview = styled.pre`
  margin: 0;
  padding: 10px 12px 28px;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', Menlo, monospace;
  font-size: 0.7rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: pre;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  /* fade out bottom */
  -webkit-mask-image: linear-gradient(to bottom, black 50%, transparent 100%);
  mask-image: linear-gradient(to bottom, black 50%, transparent 100%);
`;

const PastedFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px 4px 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgb(0 0 0 / calc(0.35 * var(--message-input-surface-opacity, 1)))'
      : 'rgb(255 255 255 / calc(0.85 * var(--message-input-surface-opacity, 1)))'};
  backdrop-filter: blur(4px);
`;

const PastedBadge = styled.span`
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#c8c8cc' : '#fff')};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.65)'};
  border-radius: 5px;
  padding: 2px 6px;
`;

const PastedCharCount = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PastedRemoveBtn = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.bgHover};
  color: ${({ theme }) => theme.colors.textMuted};
  border: none;
  cursor: pointer;
  opacity: 0;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${PastedCard}:hover & {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.bgTertiary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* Pasted modal */
const PastedModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PastedModalTextarea = styled.textarea`
  display: block;
  width: 100%;
  box-sizing: border-box;
  margin: 0;
  padding: 14px 16px;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', Menlo, monospace;
  font-size: 0.8rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  white-space: pre;
  word-break: break-word;
  resize: none;
  outline: none;
  height: 360px;
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  &:focus {
    border-color: ${({ theme }) => theme.colors.borderInput};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.borderInput}33;
  }
`;

const PastedModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PastedModalConfirmBtn = styled.button`
  padding: 9px 24px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};
  border: none;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  &:hover {
    opacity: 0.85;
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

/* ---- Pasted block type ---- */

interface PastedBlock {
  id: string;
  content: string;
  forceInvokeAgent?: boolean;
}

/** 이 글자수 이상이면 카드로 접음 */
const PASTE_COLLAPSE_THRESHOLD = 500;

type MentionKind = 'agent' | 'member';
interface MentionSuggestion {
  id: string;
  display: string;
  kind: MentionKind;
}

interface MentionMenuState {
  open: boolean;
  x: number;
  y: number;
  query: string;
  activeIndex: number;
}

interface MentionTriggerContext {
  node: Text;
  startOffset: number;
  endOffset: number;
}

const AGENT_MENTION_ID = 'agent:askyour.ai';
const AGENT_MENTION_DISPLAY = 'askyour.ai';
const GROUP_AGENT_MENTION_RE = /(^|[\s([{"'])@askyour\.ai(?=$|[\s)\]}",.!?:;'"])/i;

function normalizeEditorText(text: string): string {
  return text.replace(/\u00A0/g, ' ').replace(/\u200B/g, '');
}

function extractPlainTextFromEditorNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return normalizeEditorText((node as Text).data);
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  if (el.dataset.mentionChip === 'true') {
    const display = el.dataset.mentionDisplay || AGENT_MENTION_DISPLAY;
    return `@${display}`;
  }
  let out = '';
  node.childNodes.forEach((child) => {
    out += extractPlainTextFromEditorNode(child);
  });
  return out;
}

function extractPlainTextFromEditor(root: HTMLElement): string {
  let out = '';
  root.childNodes.forEach((node) => {
    out += extractPlainTextFromEditorNode(node);
  });
  return normalizeEditorText(out);
}

function editorHasAgentMention(root: HTMLElement): boolean {
  if (root.querySelector(`[data-mention-chip="true"][data-mention-id="${AGENT_MENTION_ID}"]`)) {
    return true;
  }
  return GROUP_AGENT_MENTION_RE.test(extractPlainTextFromEditor(root));
}

function removeMentionChipsFromEditor(root: HTMLElement): void {
  const chips = root.querySelectorAll<HTMLElement>('[data-mention-chip="true"]');
  chips.forEach((chip) => {
    const next = chip.nextSibling;
    chip.remove();
    if (next && next.nodeType === Node.TEXT_NODE) {
      const textNode = next as Text;
      textNode.data = textNode.data.replace(/^\u00A0/, '');
      if (!textNode.data) textNode.remove();
    }
  });
  root.normalize();
}

function placeCaretAtTextEnd(node: Text): void {
  const selection = window.getSelection();
  if (!selection) return;
  const nextRange = document.createRange();
  nextRange.setStart(node, node.data.length);
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
}

function insertPlainTextAtSelection(root: HTMLElement, text: string): void {
  const selection = window.getSelection();
  if (!selection) return;

  const normalized = normalizeEditorText(text);
  let range: Range;

  if (
    selection.rangeCount > 0
    && root.contains(selection.getRangeAt(0).startContainer)
    && root.contains(selection.getRangeAt(0).endContainer)
  ) {
    range = selection.getRangeAt(0);
  } else {
    range = document.createRange();
    range.selectNodeContents(root);
    range.collapse(false);
  }

  range.deleteContents();
  const textNode = document.createTextNode(normalized);
  range.insertNode(textNode);

  const nextRange = document.createRange();
  nextRange.setStart(textNode, textNode.data.length);
  nextRange.collapse(true);
  selection.removeAllRanges();
  selection.addRange(nextRange);
}

function getCaretRect(range: Range): DOMRect {
  const rect = range.getBoundingClientRect();
  if (rect.width > 0 || rect.height > 0) {
    return rect;
  }
  const marker = document.createElement('span');
  marker.textContent = '\u200B';
  range.insertNode(marker);
  const markerRect = marker.getBoundingClientRect();
  const markerParent = marker.parentNode;
  if (markerParent) markerParent.removeChild(marker);
  range.collapse(false);
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(range);
  }
  return markerRect;
}

/* ---- File type helpers ---- */

function formatFileSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface MessageInputSubmitPayload {
  text: string;
  files: FileAttachment[];
  pastedContent: string;
  reboMode: ReboMode;
  activeSkillIds?: string[];
  /** An override owns its own `sendMessage` call, so the composer's turn
   *  switches only reach the request if they travel on this payload. */
  mcpApprovalMode: 'ask_for_approval' | 'full_access';
  reasoningEnabled: boolean;
  /** Overrides forward this selection along with the rest of the turn. */
  systemPromptId?: string | null;
}

interface MessageInputProps {
  plusMenuItemIds?: readonly string[];
  modeOverride?: WorkspaceMode;
  placeholderOverride?: string;
  surfaceOpacity?: number;
  beforeSubmit?: (payload: {
    text: string;
    files: FileAttachment[];
    pastedContent: string;
  }) => boolean | Promise<boolean>;
  onSubmitOverride?: (
    payload: MessageInputSubmitPayload,
  ) => boolean | void | Promise<boolean | void>;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  modeOverride,
  placeholderOverride,
  surfaceOpacity = 1,
  plusMenuItemIds,
  beforeSubmit,
  onSubmitOverride,
}) => {
  const { t, i18n } = useTranslation('common');
  const theme = useTheme();
  const reboMode = useReboModeStore((s) => s.mode);
  const setReboMode = useReboModeStore((s) => s.setMode);
  const skillUploadEnabled = useChatFeature('skillUpload');
  const skillBrowseEnabled = useChatFeature('skillBrowse');
  const skillLoadEnabled = skillUploadEnabled || skillBrowseEnabled;
  const effectiveReboMode: ReboMode =
    reboMode === 'skill' && !skillLoadEnabled ? 'auto' : reboMode;
  const reboModeOptions = useMemo(
    () =>
      getReboModeOptions().filter(
        (option) => option.id !== 'skill' || skillLoadEnabled,
      ),
    [i18n.language, skillLoadEnabled],
  );
  const [openComposerMenu, setOpenComposerMenu] = useState<'approval' | 'model' | 'mode' | null>(null);
  const chatModel = useChatModelStore((s) => s.model);
  const setChatModel = useChatModelStore((s) => s.setModel);
  const chatModelOptions = useMemo(() => getChatModelOptions(), [i18n.language]);

  /* Interactive(HITL) 토글 — 모드 선택기와 직교하는 축이라 옆에 나란히 둔다.
   * 통합 에이전트 채팅에서만 의미가 있으므로 그 외 워크스페이스에서는 숨긴다. */
  const mcpApprovalMode = useReboModeStore((s) => s.mcpApprovalMode);
  const interactiveMode = useReboModeStore((s) => s.interactive);
  const toggleInteractiveMode = useReboModeStore((s) => s.toggleInteractive);
  const reasoningEnabled = useReboModeStore((s) => s.reasoningEnabled);
  const toggleReasoning = useReboModeStore((s) => s.toggleReasoning);

  const skillModalOpen = useSkillStore((s) => s.isModalOpen);
  const skillModalInitialTab = useSkillStore((s) => s.modalInitialTab);
  const openSkillModal = useSkillStore((s) => s.openModal);
  const closeSkillModal = useSkillStore((s) => s.closeModal);
  const activeSkillIds = useSkillStore((s) => s.activeSkillIds);
  const skillsList = useSkillStore((s) => s.skills);
  /* 마켓플레이스에서 적용한 남의 공유 스킬 — 내/공식 목록 어디에도 없다. */
  const sharedSkills = useSkillStore((s) => s.sharedSkills);
  const toggleSkill = useSkillStore((s) => s.toggleSkill);
  const officialSkills = useOfficialSkillStore((s) => s.skills);

  const navigate = useNavigate();
  const sendMessage = useChatStore((s) => s.sendMessage);
  const stopStreaming = useChatStore((s) => s.stopStreaming);
  const setPreviewFile = useChatStore((s) => s.setPreviewFile);
  const previewFile = useChatStore((s) => s.previewFile);
  const narrowPreviewView = useChatStore((s) => s.narrowPreviewView);
  const isNarrowLayout = useChatStore((s) => s.isNarrowLayout);
  const setNarrowPreviewView = useChatStore((s) => s.setNarrowPreviewView);
  const addToast = useChatStore((s) => s.addToast);
  const markQuestionAnswered = useChatStore((s) => s.markQuestionAnswered);
  const submitToolApproval = useChatStore((s) => s.submitToolApproval);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const currentUser = useUserStore((s) => s.user);
  const {
    workspaceMode: resolvedWorkspaceMode,
    conversation: currentConversation,
    runtimeConversationId,
    isStreaming,
    isUploading,
  } = useComposerTarget(modeOverride);
  const isUploadingConversation = isUploading;
  const isGroupChat = !!currentConversation?.sharedGroup;
  const currentUserId = currentUser?.userId || currentUser?.email || '';
  const systemPromptSupported = isSystemPromptMode(resolvedWorkspaceMode);
  const [systemPromptModalOpen, setSystemPromptModalOpen] = useState(false);
  const promptAccountId = useSystemPromptStore((s) => s.accountId);
  const systemPrompts = useSystemPromptStore((s) => s.prompts);
  const selectedPromptIds = useSystemPromptStore((s) => s.selectedPromptIds);
  const fetchSystemPrompts = useSystemPromptStore((s) => s.fetchPrompts);
  const selectSystemPrompt = useSystemPromptStore((s) => s.selectPrompt);
  const selectedSystemPromptId = systemPromptSupported && promptAccountId === currentUserId
    ? selectedPromptIds[resolvedWorkspaceMode]
    : null;
  const selectedSystemPrompt = systemPrompts.find((prompt) => prompt.id === selectedSystemPromptId);

  useEffect(() => {
    if (systemPromptSupported && currentUserId) void fetchSystemPrompts();
  }, [currentUserId, fetchSystemPrompts, systemPromptSupported]);

  useEffect(() => {
    setSystemPromptModalOpen(false);
  }, [resolvedWorkspaceMode, currentUserId]);

  /* Interactive 는 통합 에이전트 채팅 전용이다 (백엔드도 workspace_type
   * "agent" 에만 HITL 경로를 조립한다). 다른 워크스페이스에서는 토글을
   * 노출하지 않고 요청에도 false 로 나간다. */
  const interactiveSupported = resolvedWorkspaceMode === 'agent';
  const interactiveEnabled = interactiveSupported && interactiveMode;

  /* Reasoning 은 Interactive 와 다른 축이다. HITL 은 통합 그래프만 인터럽트
   * 게이트를 조립하지만, 추론은 답변에 대한 사용자 선호라 문서 에이전트도
   * 같은 스위치를 따른다 (백엔드도 워크스페이스로 구분하지 않는다). */
  const reasoningSupported =
    resolvedWorkspaceMode === 'agent'
    || resolvedWorkspaceMode === 'doc-agent'
    || resolvedWorkspaceMode === 'gtc-agent';
  /* 모델 선택도 같은 세 워크스페이스다. GTC 후속 채팅은 문서 에이전트 채팅과
   * 같은 통합 에이전트 위에서 도는데, 컨트롤만 빠져 있어 "왜 여기만 못 고르나"
   * 가 됐다. */
  const showChatModelPicker = reasoningSupported;
  /* 컨트롤 접기(tier: full → compact → narrow)는 워크스페이스를 가리지 않고
   * 실제 남는 폭으로 판단한다 — 태블릿·작은 노트북 뷰포트, 문서 에이전트
   * 플로팅 패널, GTC/선적 결과 화면의 우측 패널 모두 같은 규칙. live-agent 는
   * 모드 셀렉터 대신 고정 라벨을 쓰므로 요약 버튼으로 대체할 수 없어 제외한다. */
  const { cardRef, rowRef, leftRef, tier: composerTier } = useComposerTier(
    resolvedWorkspaceMode !== 'live-agent',
  );

  // Close the old layout's menus when the splitter changes the control layout.
  useEffect(() => {
    setOpenComposerMenu(null);
  }, [composerTier]);

  /* 답을 기다리는 HITL 질문. 재개 요청을 낙관적으로 append한 뒤에도 서버의
   * post-reservation ACK 전까지 기존 카드가 유지되어야 하므로 뒤에서부터
   * pendingQuestion을 찾는다. 일반 메시지는 store가 기존 질문을 폐기한다. */
  const pendingQuestionMessage = useMemo(() => {
    const messages = currentConversation?.messages;
    if (!messages || messages.length === 0) return null;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role === 'assistant' && message.pendingQuestion) {
        return message;
      }
    }
    return null;
  }, [currentConversation?.messages]);

  const [editorPlainText, setEditorPlainText] = useState('');
  const [editorIsEmpty, setEditorIsEmpty] = useState(true);
  const [editorAtMax, setEditorAtMax] = useState(false);
  const [forceInvokeAgentOnce, setForceInvokeAgentOnce] = useState(false);
  const [files, setFiles] = useState<FileAttachment[]>([]);
  const [pastedBlocks, setPastedBlocks] = useState<PastedBlock[]>([]);
  const [viewingBlock, setViewingBlock] = useState<PastedBlock | null>(null);
  const [focused, setFocused] = useState(false);
  const [mentionMenu, setMentionMenu] = useState<MentionMenuState>({
    open: false,
    x: 0,
    y: 0,
    query: '',
    activeIndex: 0,
  });
  const [plusMenu, setPlusMenu] = useState<{ open: boolean; x: number; y: number }>({ open: false, x: 0, y: 0 });
  const [workspaceFilesModal, setWorkspaceFilesModal] = useState<{
    open: boolean;
    mode?: WorkspaceMode;
    agentId?: string;
    agentLabel: string;
  }>({ open: false, agentLabel: '' });
  const [wikiPickerOpen, setWikiPickerOpen] = useState(false);
  const WikiSourcePicker = getWikiSourcePickerComponent();

  /* 대화에 붙은 프로젝트 위키. 저장 위치는 대화 settings 이고, 아직 대화가 없는
   * 새 채팅만 임시 자리를 본다 (``lib/projectWikiBinding.ts``). */
  const projectWikiDraft = useProjectWikiDraft((s) => s.draft);
  /* 통합 에이전트에서만 본다. AI 서버도 ``workspace_type`` 이 ``agent`` 인 요청만
   * 이 바인딩을 읽으므로, 다른 워크스페이스에서 칩을 띄우면 붙지도 않은 위키가
   * 붙은 것처럼 보인다 (특히 아직 대화가 없는 화면에서 임시 값이 새어 보인다). */
  const projectWikiBinding =
    resolvedWorkspaceMode === 'agent'
      ? resolveProjectWikiBinding(currentConversation, projectWikiDraft)
      : null;
  const handleBindProjectWiki = useCallback(
    (binding: ProjectWikiBinding | null) => {
      bindProjectWiki(runtimeConversationId, binding);
    },
    [runtimeConversationId],
  );
  /* 새 채팅에서 고른 위키를 방금 만들어진 대화로 옮긴다. 이걸 안 하면 첫 메시지
   * 이후 칩이 사라지고, 다음 턴부터 위키가 빠진 요청이 나간다. */
  useEffect(() => {
    if (runtimeConversationId && projectWikiDraft) {
      adoptDraftProjectWiki(runtimeConversationId);
    }
  }, [runtimeConversationId, projectWikiDraft]);

  /* ---- 컨텍스트 칩 행 표시 여부 ----
   * 세 종류(시스템 프롬프트 · 위키 · 스킬)가 한 행을 공유하므로 하나라도 있을
   * 때만 행을 그린다. 그래야 빈 행의 상단 여백이 입력창에 남지 않는다. */
  const showSystemPromptChip = systemPromptSupported && Boolean(selectedSystemPromptId);
  const systemPromptChipLabel = t('chat.systemPromptChip', {
    defaultValue: '프롬프트: {{name}}',
    name: selectedSystemPrompt?.name ?? t('chat.systemPrompt', { defaultValue: '시스템 프롬프트' }),
  });
  const projectWikiChipLabel = projectWikiBinding
    ? t('chat.projectWikiChip', {
        defaultValue: '위키: {{name}}',
        name: projectWikiBinding.title || projectWikiBinding.wikiId,
      })
    : '';
  const showSkillChips = effectiveReboMode === 'skill' && activeSkillIds.length > 0;
  const hasContextChips = showSystemPromptChip || projectWikiBinding !== null || showSkillChips;
  const [modalCenterPadding, setModalCenterPadding] = useState({ left: 0, right: 0 });
  const outerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const mentionTriggerRef = useRef<MentionTriggerContext | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FileAttachment[]>([]);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const mentionSuggestions = useMemo<MentionSuggestion[]>(() => {
    if (!isGroupChat || !currentConversation?.sharedGroup) return [];
    const memberSuggestions: MentionSuggestion[] = currentConversation.sharedGroup.members
      .filter((m) => m.user_id !== currentUserId)
      .map((m) => {
        const display = (m.name?.trim() || m.email?.split('@')[0] || t('chat.teamMember')).trim();
        return {
          id: `member:${m.user_id}`,
          display,
          kind: 'member',
        };
      });
    return [
      { id: AGENT_MENTION_ID, display: AGENT_MENTION_DISPLAY, kind: 'agent' },
      ...memberSuggestions,
    ];
  }, [currentConversation, currentUserId, isGroupChat]);

  const composerPlaceholder = placeholderOverride ?? (isGroupChat
    ? t('chat.mentionPlaceholder')
    : t('chat.messagePlaceholder'));

  const filteredMentionSuggestions = useMemo(() => {
    const query = mentionMenu.query.trim().toLowerCase();
    if (!query) return mentionSuggestions;
    return mentionSuggestions.filter((item) =>
      item.display.toLowerCase().includes(query),
    );
  }, [mentionMenu.query, mentionSuggestions]);

  const resetComposerForDraft = useCallback((focusInput = false) => {
    const editor = editorRef.current;
    if (editor) editor.innerHTML = '';
    setEditorPlainText('');
    setEditorIsEmpty(true);
    setEditorAtMax(false);
    setForceInvokeAgentOnce(false);
    mentionTriggerRef.current = null;
    setMentionMenu((prev) => ({ ...prev, open: false, query: '', activeIndex: 0 }));
    setFiles((prev) => {
      prev.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
      return [];
    });
    setPastedBlocks([]);
    setPreviewFile(null);
    if (focusInput) {
      setTimeout(() => editorRef.current?.focus(), 0);
    }
  }, [setPreviewFile]);

  /* 워크스페이스 모드 변경 시 첨부 파일 초기화 */
  const prevWorkspaceMode = useRef(resolvedWorkspaceMode);
  useEffect(() => {
    if (prevWorkspaceMode.current !== resolvedWorkspaceMode) {
      resetComposerForDraft();
    }
    prevWorkspaceMode.current = resolvedWorkspaceMode;
  }, [resolvedWorkspaceMode, resetComposerForDraft]);

  useEffect(() => {
    const host = outerRef.current;
    if (!host) return;

    const layoutMain = host.closest('main');
    if (!(layoutMain instanceof HTMLElement)) {
      setModalCenterPadding((prev) => (
        prev.left === 0 && prev.right === 0 ? prev : { left: 0, right: 0 }
      ));
      return;
    }

    let rafId: number | null = null;
    const update = () => {
      rafId = null;
      const rect = layoutMain.getBoundingClientRect();
      const next = {
        left: Math.max(0, Math.round(rect.left)),
        right: Math.max(0, Math.round(window.innerWidth - rect.right)),
      };
      setModalCenterPadding((prev) => (
        prev.left === next.left && prev.right === next.right ? prev : next
      ));
    };
    const schedule = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(update);
    };

    schedule();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(layoutMain);
    window.addEventListener('resize', schedule);
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [resolvedWorkspaceMode]);

  /* 현재 입력창이 연결된 대화 전환 시 첨부 파일 초기화 */
  const prevConversationId = useRef(runtimeConversationId);
  const prevConversationRef = useRef<typeof currentConversation>(currentConversation);
  useEffect(() => {
    const prevId = prevConversationId.current;
    if (prevId !== runtimeConversationId) {
      const prevConv = prevConversationRef.current;
      const keepComposer =
        runtimeConversationId === null
        && !!prevConv
        && !prevConv.messages.some((msg) => msg.role === 'user' || msg.role === 'assistant');
      if (!keepComposer) {
        resetComposerForDraft();
      }
    }
    prevConversationId.current = runtimeConversationId;
    prevConversationRef.current = currentConversation;
  }, [runtimeConversationId, currentConversation, resetComposerForDraft]);

  /* P11-5e: hoisted up so `updateMentionMenuFromSelection` (defined a few
   * lines below) can reference it without a TDZ violation. The full
   * feature-flag bundle is still consumed below for plus-menu logic. */
  // PR-FE-1: was `useFeatureFlags().isEnabled('mode.agent.groupChat')` —
  // ``useChatFeature`` resolves the namespace via the active
  // :data:`ChatModeContext` (defaults to ``agent`` so legacy callers
  // are unaffected; doc-agent renders this ChatArea with mode prop
  // and gets ``mode.doc-agent.groupChat`` automatically).
  const groupChatMentionEnabled = useChatFeature('groupChat');

  const closeMentionMenu = useCallback(() => {
    mentionTriggerRef.current = null;
    setMentionMenu((prev) =>
      prev.open
        ? { ...prev, open: false, query: '', activeIndex: 0 }
        : prev,
    );
  }, []);

  const syncEditorState = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) {
      setEditorPlainText('');
      setEditorIsEmpty(true);
      setEditorAtMax(false);
      return;
    }
    const plain = extractPlainTextFromEditor(editor);
    setEditorPlainText(plain);
    setEditorIsEmpty(plain.trim().length === 0);
    const maxH = parseInt(getComputedStyle(editor).maxHeight, 10) || 200;
    const atMax = editor.scrollHeight > maxH + 1;
    setEditorAtMax(atMax);
    editor.style.overflowY = atMax ? 'auto' : 'hidden';
  }, []);

  const updateMentionMenuFromSelection = useCallback(() => {
    /* P11-5e: when the tenant has disabled `mode.agent.groupChat` the
     * mention picker is suppressed even if the conversation has a
     * shared group attached (legacy data). */
    if (!groupChatMentionEnabled || !isGroupChat || mentionSuggestions.length === 0) {
      closeMentionMenu();
      return;
    }
    const editor = editorRef.current;
    const selection = window.getSelection();
    if (!editor || !selection || !selection.rangeCount || !selection.isCollapsed) {
      closeMentionMenu();
      return;
    }
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.startContainer) || range.startContainer.nodeType !== Node.TEXT_NODE) {
      closeMentionMenu();
      return;
    }
    const node = range.startContainer as Text;
    const endOffset = range.startOffset;
    const before = node.data.slice(0, endOffset);
    const match = /(?:^|[\s([{"'])@([^\s@]*)$/.exec(before);
    if (!match) {
      closeMentionMenu();
      return;
    }
    const query = match[1] ?? '';
    const startOffset = endOffset - query.length - 1;
    if (startOffset < 0) {
      closeMentionMenu();
      return;
    }
    const filtered = mentionSuggestions.filter((item) =>
      item.display.toLowerCase().includes(query.toLowerCase()),
    );
    if (filtered.length === 0) {
      closeMentionMenu();
      return;
    }
    mentionTriggerRef.current = { node, startOffset, endOffset };
    const rect = getCaretRect(range.cloneRange());
    const x = Math.max(8, Math.min(rect.left, window.innerWidth - 340));
    const y = Math.max(8, rect.top - 8);
    setMentionMenu((prev) => ({
      ...prev,
      open: true,
      query,
      x,
      y,
      activeIndex: Math.min(prev.activeIndex, filtered.length - 1),
    }));
  }, [closeMentionMenu, groupChatMentionEnabled, isGroupChat, mentionSuggestions]);

  const insertMentionChip = useCallback((mention: MentionSuggestion) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const trigger = mentionTriggerRef.current;
    if (!editor || !selection || !selection.rangeCount || !trigger || !trigger.node.isConnected) return;
    const currentRange = selection.getRangeAt(0);
    const replaceRange = document.createRange();
    replaceRange.setStart(trigger.node, trigger.startOffset);
    if (currentRange.startContainer === trigger.node && currentRange.startOffset >= trigger.startOffset) {
      replaceRange.setEnd(trigger.node, currentRange.startOffset);
    } else {
      replaceRange.setEnd(trigger.node, trigger.endOffset);
    }
    replaceRange.deleteContents();

    const chip = document.createElement('span');
    chip.dataset.mentionChip = 'true';
    chip.dataset.mentionId = mention.id;
    chip.dataset.mentionDisplay = mention.display;
    chip.dataset.mentionKind = mention.kind;
    chip.contentEditable = 'false';
    chip.textContent = `@${mention.display}`;

    const spacer = document.createTextNode('\u00A0');
    replaceRange.insertNode(spacer);
    replaceRange.insertNode(chip);

    editor.normalize();
    placeCaretAtTextEnd(spacer);
    syncEditorState();
    closeMentionMenu();
    editor.focus();
  }, [closeMentionMenu, syncEditorState]);

  useEffect(() => {
    if (!mentionMenu.open) return;
    if (filteredMentionSuggestions.length === 0) {
      closeMentionMenu();
      return;
    }
    setMentionMenu((prev) => (
      prev.activeIndex >= filteredMentionSuggestions.length
        ? { ...prev, activeIndex: filteredMentionSuggestions.length - 1 }
        : prev
    ));
  }, [closeMentionMenu, filteredMentionSuggestions.length, mentionMenu.open]);

  const handleEditorInput = useCallback(() => {
    syncEditorState();
    updateMentionMenuFromSelection();
  }, [syncEditorState, updateMentionMenuFromSelection]);

  const removePastedBlock = (id: string) => {
    setPastedBlocks((prev) => {
      const next = prev.filter((b) => b.id !== id);
      setForceInvokeAgentOnce(next.some((b) => !!b.forceInvokeAgent));
      return next;
    });
  };

  const notifyAttachmentConstraintViolations = useCallback((
    rejectedBySize: readonly { name?: string; size?: number | null }[],
    rejectedByCount: readonly { name?: string; size?: number | null }[],
    rejectedByType?: readonly { name?: string; size?: number | null }[],
  ) => {
    if (rejectedByType && rejectedByType.length > 0) {
      const toast = buildAttachmentTypeLimitToast(rejectedByType);
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
    }

    if (rejectedBySize.length > 0) {
      const toast = buildAttachmentSizeLimitToast(rejectedBySize);
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
    }

    if (rejectedByCount.length > 0) {
      const toast = buildAttachmentCountLimitToast(rejectedByCount.length);
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
    }
  }, [addToast]);

  const appendAttachmentEntries = useCallback((incoming: FileAttachment[]) => {
    if (incoming.length === 0) return;

    const { accepted, rejectedBySize, rejectedByCount, rejectedByType } = validateAttachmentBatch(
      incoming,
      filesRef.current.length,
    );

    for (const rejected of [...rejectedBySize, ...rejectedByCount, ...rejectedByType]) {
      if (rejected.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(rejected.previewUrl);
      }
    }

    notifyAttachmentConstraintViolations(rejectedBySize, rejectedByCount, rejectedByType);

    if (accepted.length === 0) return;
    filesRef.current = [...filesRef.current, ...accepted];
    setFiles((prev) => [...prev, ...accepted]);
  }, [notifyAttachmentConstraintViolations]);

  const addFiles = useCallback((incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const fileList = Array.isArray(incoming) ? incoming : Array.from(incoming);
    const { accepted, rejectedBySize, rejectedByCount, rejectedByType } = validateAttachmentBatch(
      fileList,
      filesRef.current.length,
    );

    notifyAttachmentConstraintViolations(rejectedBySize, rejectedByCount, rejectedByType);

    if (accepted.length === 0) return;

    const arr: FileAttachment[] = accepted.map((f) => ({
      id: uuid(),
      name: f.name,
      type: f.type,
      size: f.size,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
      file: f,
    }));
    filesRef.current = [...filesRef.current, ...arr];
    setFiles((prev) => [...prev, ...arr]);
  }, [notifyAttachmentConstraintViolations]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    const clipboardFiles = extractClipboardFiles(e.clipboardData);
    if (clipboardFiles.length > 0) {
      e.preventDefault();
      addFiles(clipboardFiles);
      closeMentionMenu();
      return;
    }

    const pasted = e.clipboardData.getData('text/plain') || e.clipboardData.getData('text');
    if (!pasted) return;

    if (pasted.length >= PASTE_COLLAPSE_THRESHOLD) {
      e.preventDefault();
      const editor = editorRef.current;
      let shouldForceInvoke = false;
      if (editor) {
        shouldForceInvoke = editorHasAgentMention(editor);
        if (shouldForceInvoke) {
          setForceInvokeAgentOnce(true);
        }
        removeMentionChipsFromEditor(editor);
        syncEditorState();
      }
      setPastedBlocks((prev) => [
        ...prev,
        { id: uuid(), content: pasted, forceInvokeAgent: shouldForceInvoke },
      ]);
      closeMentionMenu();
      return;
    }

    e.preventDefault();
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    insertPlainTextAtSelection(editor, pasted);
    syncEditorState();
    updateMentionMenuFromSelection();
  }, [addFiles, closeMentionMenu, syncEditorState, updateMentionMenuFromSelection]);

  useEffect(() => {
    return onAppEvent('chat:addFiles', (detail) => {
      if (detail?.files && detail.files.length > 0) {
        addFiles(detail.files);
      }
    });
  }, [addFiles]);

  useEffect(() => {
    return onAppEvent('chat:addMailAttachments', (detail) => {
      if (detail?.files && detail.files.length > 0) {
        appendAttachmentEntries(detail.files);
      }
    });
  }, [appendAttachmentEntries]);

  const removeFile = (id: string) => {
    if (previewFile?.id === id) setPreviewFile(null);
    setFiles((prev) => {
      const f = prev.find((a) => a.id === id);
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  const resetComposer = () => {
    const editor = editorRef.current;
    if (editor) editor.innerHTML = '';
    setEditorPlainText('');
    setEditorIsEmpty(true);
    setEditorAtMax(false);
    setForceInvokeAgentOnce(false);
    closeMentionMenu();
    files.forEach((f) => {
      if (f.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(f.previewUrl);
      }
    });
    setFiles([]);
    setPastedBlocks([]);
    /* 모바일에서만 미리보기 닫기 — 데스크톱에서는 유지 */
    if (window.matchMedia(`(max-width: ${theme.breakpoints.mobile})`).matches) {
      setPreviewFile(null);
    }
    setTimeout(() => editorRef.current?.focus(), 0);
  };

  const handleSubmit = async () => {
    const trimmed = editorPlainText.trim();
    if (!trimmed && files.length === 0 && pastedBlocks.length === 0) return;

    const pastedContent = pastedBlocks.map((b) => b.content).join('\n\n');
    const fullText = [trimmed, pastedContent].filter(Boolean).join('\n\n');
    const draftPayload = {
      text: fullText,
      files: [...files],
      pastedContent,
    };

    if (beforeSubmit) {
      const shouldContinue = await beforeSubmit(draftPayload);
      if (!shouldContinue) return;
    }

    const submittedFiles = cloneAttachmentsForSubmit(draftPayload.files);
    const turnReasoningEnabled = reasoningSupported ? reasoningEnabled : true;
    const turnSystemPromptId = getSelectedSystemPromptId(resolvedWorkspaceMode);
    const submitPayload = {
      ...draftPayload,
      files: submittedFiles,
      reboMode: effectiveReboMode,
      ...(effectiveReboMode === 'skill' && activeSkillIds.length > 0
        ? { activeSkillIds: [...activeSkillIds] }
        : {}),
      mcpApprovalMode,
      reasoningEnabled: turnReasoningEnabled,
      systemPromptId: turnSystemPromptId,
    };

    if (resolvedWorkspaceMode === 'agent') {
      emitAppEvent('agent:anchorLatestUser');
    }

    const shouldForceInvokeAgent =
      forceInvokeAgentOnce || pastedBlocks.some((b) => !!b.forceInvokeAgent);
    const sendOptions: {
      forceInvokeAgent?: boolean;
      modeOverride?: WorkspaceMode;
      reboMode?: ReboMode;
      activeSkillIds?: string[];
      mcpApprovalMode?: 'ask_for_approval' | 'full_access';
      interactiveMode?: boolean;
      reasoningEnabled?: boolean;
      systemPromptId?: string | null;
      requestMetadata?: Record<string, unknown> | null;
    } = {};
    if (shouldForceInvokeAgent) {
      sendOptions.forceInvokeAgent = true;
    }
    sendOptions.modeOverride = resolvedWorkspaceMode;
    sendOptions.reboMode = effectiveReboMode;
    sendOptions.mcpApprovalMode = mcpApprovalMode;
    sendOptions.interactiveMode = interactiveEnabled;
    sendOptions.reasoningEnabled = turnReasoningEnabled;
    sendOptions.systemPromptId = turnSystemPromptId;
    // 대화에 붙은 프로젝트 위키를 매 턴 실어 보낸다. AI 서버는 이 값이 있는
    // 요청에만 WikiAgent 를 워커 풀에 넣으므로, 빠지면 위키를 붙였는데 에이전트가
    // 조회할 수 없는 턴이 된다.
    const wikiMeta = projectWikiRequestMetadata(projectWikiBinding);
    if (wikiMeta) {
      sendOptions.requestMetadata = { project_wiki: wikiMeta };
    }
    if (effectiveReboMode === 'skill' && activeSkillIds.length > 0) {
      sendOptions.activeSkillIds = activeSkillIds;
    }

    if (onSubmitOverride) {
      const accepted = await onSubmitOverride(submitPayload);
      if (accepted === false) {
        // cloneAttachmentsForSubmit가 만든 임시 URL만 해제한다. 원본 composer
        // attachment는 유지해 사용자가 저장 복구 뒤 그대로 재전송할 수 있다.
        for (const attachment of submittedFiles) {
          if (attachment.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(attachment.previewUrl);
          }
        }
        return;
      }
    } else if (resolvedWorkspaceMode === 'live-agent') {
      const { sendTextFn } = (await import('@auw/stores')).useLiveAgentStore.getState();
      if (sendTextFn && fullText.trim()) {
        sendTextFn(fullText.trim());
      }
    } else {
      sendMessage(
        fullText,
        submittedFiles.length > 0 ? submittedFiles : undefined,
        Object.keys(sendOptions).length > 0 ? sendOptions : undefined,
      );
    }
    resetComposer();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    /* 한글 등 IME 조합 중에는 Enter 무시 (마지막 글자 중복 방지) */
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;

    if (mentionMenu.open && filteredMentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionMenu((prev) => ({
          ...prev,
          activeIndex: (prev.activeIndex + 1) % filteredMentionSuggestions.length,
        }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionMenu((prev) => ({
          ...prev,
          activeIndex:
            (prev.activeIndex - 1 + filteredMentionSuggestions.length)
            % filteredMentionSuggestions.length,
        }));
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        const picked = filteredMentionSuggestions[mentionMenu.activeIndex];
        if (picked) insertMentionChip(picked);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMentionMenu();
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      if (isBusy) return;
      e.preventDefault();
      void handleSubmit();
    }
  };

  const canSend = editorPlainText.trim().length > 0 || files.length > 0 || pastedBlocks.length > 0;
  const isBusy = isStreaming || isUploadingConversation;
  const safeSurfaceOpacity = Math.min(1, Math.max(0.35, surfaceOpacity));

  const handlePlusClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPlusMenu({ open: true, x: rect.left, y: rect.top - 4 });
  };

  const { isEnabled: isFeatureEnabled } = useFeatureFlags();
  const workspaceFileModes = useMemo(
    () =>
      (['agent', 'doc-agent'] as const).filter((mode) =>
        isFeatureEnabled(`mode.${mode}`),
      ),
    [isFeatureEnabled],
  );

  /* P11-5e: `groupChatMentionEnabled` is declared near the top of the
   * component to satisfy TDZ ordering with `updateMentionMenuFromSelection`. */

  const pluginMenuItems = usePluginMenuItems({
    open: plusMenu.open,
    enabled: isFeatureEnabled('mode.plugin'),
    conversation: currentConversation,
    mode: resolvedWorkspaceMode,
  });
  const plusMenuItems: DropdownMenuItem[] = [
    {
      id: 'local-upload',
      label: t('chat.localUpload'),
      icon: <Upload size={15} />,
      onClick: () => fileRef.current?.click(),
    },
    ...(systemPromptSupported
      ? [{
          id: 'system-prompt',
          label: t('chat.systemPrompt', { defaultValue: '시스템 프롬프트' }),
          icon: <SlidersHorizontal size={15} />,
          onClick: () => setSystemPromptModalOpen(true),
        } satisfies DropdownMenuItem]
      : []),
    ...(skillLoadEnabled
      ? [
          {
            id: 'skill-load',
            label: t('chat.skillLoad'),
            icon: <Sparkles size={15} />,
            onClick: () => {},
            children: [
              ...(skillUploadEnabled
                ? [
                    {
                      id: 'skill-upload-my',
                      label: t('chat.skillUploadMy'),
                      icon: <Upload size={15} />,
                      onClick: () => {
                        openSkillModal('my');
                      },
                    } satisfies DropdownMenuItem,
                  ]
                : []),
              ...(skillBrowseEnabled
                ? [
                    {
                      id: 'skill-browse',
                      label: t('chat.skillBrowse'),
                      icon: <BookOpen size={15} />,
                      onClick: () => {
                        openSkillModal('ref');
                      },
                    } satisfies DropdownMenuItem,
                  ]
                : []),
            ],
          } satisfies DropdownMenuItem,
        ]
      : []),
    ...(resolvedWorkspaceMode === 'agent' && workspaceFileModes.length > 0
      ? [
          {
            id: 'workspace-files',
            label: t('chat.workspaceFiles', { defaultValue: '파일 저장소' }),
            icon: <HardDrive size={15} />,
            onClick: () => {},
            children: workspaceFileModes.map((mode) => {
              const label = t(WORKSPACE_MODES[mode].labelKey);
              return {
                id: `workspace-files:${mode}`,
                label,
                icon: <FolderOpen size={15} />,
                onClick: () =>
                  setWorkspaceFilesModal({
                    open: true,
                    ...(mode === 'doc-agent'
                      ? { agentId: 'doc-agent' }
                      : { mode }),
                    agentLabel: label,
                  }),
              } satisfies DropdownMenuItem;
            }),
          } satisfies DropdownMenuItem,
        ]
      : []),
    ...pluginMenuItems,
    // 문서에이전트에서 만든 프로젝트 위키를 통합에이전트 대화에 붙인다. 피커는
    // 호스트가 슬롯에 등록하므로, 등록하지 않은 호스트에는 항목이 없다.
    ...(resolvedWorkspaceMode === 'agent' && WikiSourcePicker
      ? [
          {
            id: 'project-wiki',
            label: t('chat.projectWiki', { defaultValue: '프로젝트 위키' }),
            icon: <BookOpen size={15} />,
            onClick: () => setWikiPickerOpen(true),
          } satisfies DropdownMenuItem,
        ]
      : []),
  ];

  useEffect(() => {
    if (!isGroupChat) {
      closeMentionMenu();
    }
  }, [closeMentionMenu, isGroupChat]);

  const handleEditorFocus = () => {
    setFocused(true);
    updateMentionMenuFromSelection();
  };

  const handleEditorBlur = () => {
    setFocused(false);
    setTimeout(() => {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || !editor.contains(selection.focusNode)) {
        closeMentionMenu();
      }
    }, 0);
  };

  const handleEditorClick = () => {
    updateMentionMenuFromSelection();
  };

  /* HITL 응답 전송.
   *
   * MCP 실행 승인은 원래 도구 호출로 직접 전달한다. 일반 명확화 질문은
   * 기존 구조화 hitlResponse를 포함한 채팅 턴으로 재개한다. */
  const submitHitlAnswer = useCallback(
    (selection: HitlSelection) => {
      const message = pendingQuestionMessage;
      const request = message?.pendingQuestion;
      if (!message || !request || !runtimeConversationId) return;

      const answer = buildHitlAnswer(request, selection);
      if (request.origin?.resume_mode === 'in_place') {
        if (approvalSubmitting) return;
        setApprovalSubmitting(true);
        void submitToolApproval(runtimeConversationId, message.id, answer)
          .catch((error) => addToast({ type: 'error', title: t('common:error.connectionError'),
            description: error instanceof Error ? error.message : String(error) }))
          .finally(() => setApprovalSubmitting(false));
        return;
      }

      const text = renderHitlAnswerText(answer, {
        skipped: t('hitl.skippedMessage'),
      });
      sendMessage(text, undefined, {
        modeOverride: resolvedWorkspaceMode,
        reboMode: effectiveReboMode,
        mcpApprovalMode,
        interactiveMode: interactiveEnabled,
        reasoningEnabled,
        systemPromptId: getSelectedSystemPromptId(resolvedWorkspaceMode),
        hitlResponse: answer,
        hitlLifecycle: {
          onAccepted: () => {
            // The store invokes this only for the explicit ACK whose request
            // and continuation identifiers exactly match `answer`.
            markQuestionAnswered(runtimeConversationId, message.id, answer);
          },
          onRejected: () => {
            // Keep pendingQuestion intact so the same card can be retried.
          },
        },
        ...(effectiveReboMode === 'skill' && activeSkillIds.length > 0
          ? { activeSkillIds }
          : {}),
      });
    },
    [
      activeSkillIds,
      addToast,
      approvalSubmitting,
      effectiveReboMode,
      markQuestionAnswered,
      pendingQuestionMessage,
      mcpApprovalMode,
      interactiveEnabled,
      reasoningEnabled,
      resolvedWorkspaceMode,
      runtimeConversationId,
      sendMessage,
      submitToolApproval,
      t,
    ],
  );

  return (
    <OuterWrapper ref={outerRef} $surfaceOpacity={safeSurfaceOpacity}>
      <Wrapper>
        {/* HITL: 에이전트가 되물은 질문 — 컴포저 바로 위에 뜬다 */}
        {pendingQuestionMessage?.pendingQuestion && (
          <HitlCardSlot>
            <HitlQuestionCard
              request={pendingQuestionMessage.pendingQuestion}
              disabled={pendingQuestionMessage.pendingQuestion.origin?.resume_mode === 'in_place'
                ? approvalSubmitting || !isStreaming
                : isStreaming}
              onSubmit={(selection) => submitHitlAnswer(selection)}
              onSkip={() =>
                submitHitlAnswer({ selectedOptionIds: [], freeText: '', skipped: true })
              }
            />
          </HitlCardSlot>
        )}
        <InputCard ref={cardRef} $focus={focused}>
          {/* Upload progress */}
          {isUploadingConversation && (
            <UploadingBadge>
              <Loader size={14} />
              {t('chat.fileUploading')}
            </UploadingBadge>
          )}

          {/* 컨텍스트 칩 — 시스템 프롬프트 · 프로젝트 위키 · 스킬. 이번 턴이 아니라
           * 대화(모드) 전체에 걸리는 범위라 첨부 파일 행 위에 한 행으로 모아 둔다.
           * 세 종류가 같은 `BrandChip` 을 쓰므로 모양·높이·"종류: 이름" 라벨이
           * 같다. */}
          {hasContextChips && (
            <ContextChipRow>
              {showSystemPromptChip && (
                <BrandChip
                  size="sm"
                  icon={<SlidersHorizontal />}
                  title={systemPromptChipLabel}
                  onClick={() => setSystemPromptModalOpen(true)}
                  clickLabel={t('chat.systemPromptManage', { defaultValue: '시스템 프롬프트 관리' })}
                  onRemove={() => selectSystemPrompt(resolvedWorkspaceMode, null)}
                  removeLabel={t('chat.systemPromptClear', { defaultValue: '시스템 프롬프트 선택 해제' })}
                >
                  {systemPromptChipLabel}
                </BrandChip>
              )}
              {projectWikiBinding && (
                <BrandChip
                  size="sm"
                  icon={<BookOpen />}
                  title={projectWikiChipLabel}
                  /* 피커를 등록한 호스트에서만 본문을 눌러 다른 위키로 바꿀 수 있다. */
                  onClick={WikiSourcePicker ? () => setWikiPickerOpen(true) : undefined}
                  clickLabel={t('chat.projectWiki', { defaultValue: '프로젝트 위키' })}
                  onRemove={() => handleBindProjectWiki(null)}
                  removeLabel={t('chat.projectWikiUnbind', { defaultValue: '위키 연결 해제' })}
                >
                  {projectWikiChipLabel}
                </BrandChip>
              )}
              {showSkillChips &&
                activeSkillIds.map((sid) => {
                  const sk = skillsList.find((s) => s.skill_id === sid);
                  const offSk = !sk ? officialSkills.find((s) => s.skill_id === sid) : null;
                  const sharedSk = !sk && !offSk ? sharedSkills.find((s) => s.skill_id === sid) : null;
                  const name =
                    sk?.name
                    ?? (offSk ? resolveLocalized(offSk.display_name, i18n.language, offSk.name) : null)
                    ?? sharedSk?.name
                    ?? sid;
                  const label = t('chat.skillChip', { defaultValue: '스킬: {{name}}', name });
                  return (
                    <BrandChip
                      key={sid}
                      size="sm"
                      icon={<Sparkles />}
                      title={label}
                      /* 내 스킬이면 "내 스킬" 탭, 공식 스킬이면 레퍼런스 탭을 연다.
                       * 사내 공유 스킬은 모달에 탭이 없으므로 마켓플레이스 공유 탭으로 간다. */
                      onClick={() => {
                        if (sharedSk) navigate({ pathname: modeToPath('skill-marketplace'), search: '?tab=shared' });
                        else openSkillModal(sk ? 'my' : 'ref');
                      }}
                      clickLabel={t('chat.skillLoad')}
                      onRemove={() => { toggleSkill(sid); setReboMode('auto'); }}
                      removeLabel={t('chat.skillClear', { defaultValue: '스킬 해제' })}
                    >
                      {label}
                    </BrandChip>
                  );
                })}
            </ContextChipRow>
          )}

          {/* Attachments */}
          {(files.length > 0 || pastedBlocks.length > 0) && (
            <Attachments>
              {/* Pasted text blocks */}
              {pastedBlocks.map((block) => (
                <PastedCard key={block.id} onClick={() => setViewingBlock(block)}>
                  <PastedPreview>{block.content}</PastedPreview>
                  <PastedFooter>
                    <PastedBadge>PASTED</PastedBadge>
                    <PastedCharCount>{block.content.length.toLocaleString()}{t('chat.charCount')}</PastedCharCount>
                  </PastedFooter>
                  <PastedRemoveBtn
                    onClick={(e) => { e.stopPropagation(); removePastedBlock(block.id); }}
                  >
                    <X size={10} />
                  </PastedRemoveBtn>
                </PastedCard>
              ))}

              {files.map((f) => {
                const isImage = f.type.startsWith('image/');
                const previewable = canPreviewFile(f.name);
                return (
                  <FileCard key={f.id} $previewable={previewable}>
                    {isImage && f.previewUrl ? (
                      <ImageThumb src={f.previewUrl} alt={f.name} />
                    ) : (
                      <FileTypeBadge name={f.name} type={f.type} />
                    )}
                    <FileInfo>
                      <FileName title={f.name}>{f.name}</FileName>
                      <FileSize>{formatFileSize(f.size)}</FileSize>
                    </FileInfo>
                    <CardActions>
                      {previewable && (() => {
                        // 렌더링이 실제로 보이는 상태:
                        // wide → previewFile이 이 파일로 설정된 경우
                        // narrow → previewFile이 설정됐고 preview 뷰인 경우
                        const isRendering =
                          previewFile?.id === f.id &&
                          (!isNarrowLayout || narrowPreviewView === 'preview');
                        return isRendering ? (
                          <Tooltip label={t('tooltip.collapse')}>
                            <CardActionBtn
                              onClick={() =>
                                isNarrowLayout
                                  ? setNarrowPreviewView('chat')
                                  : setPreviewFile(null)
                              }
                            >
                              <Minimize2 size={12} />
                            </CardActionBtn>
                          </Tooltip>
                        ) : (
                          <Tooltip label={t('tooltip.expand')}>
                            <CardActionBtn onClick={() => setPreviewFile(f)}>
                              <Maximize2 size={12} />
                            </CardActionBtn>
                          </Tooltip>
                        );
                      })()}
                      <Tooltip label={t('button.delete')}>
                      <CardActionBtn onClick={() => removeFile(f.id)}>
                        <X size={12} />
                      </CardActionBtn>
                      </Tooltip>
                    </CardActions>
                  </FileCard>
                );
              })}
            </Attachments>
          )}

          <HiddenInput
            ref={fileRef}
            type="file"
            multiple
            onChange={(e) => {
              addFiles(e.target.files);
              e.currentTarget.value = '';
            }}
          />

          {/* 텍스트 입력 (그룹채팅 멘션 자동완성/칩 지원) */}
          <ComposerInputWrap>
            <EditorInput
              ref={editorRef}
              contentEditable={!isUploadingConversation}
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              data-empty={editorIsEmpty ? 'true' : 'false'}
              data-at-max={editorAtMax ? 'true' : 'false'}
              onInput={handleEditorInput}
              onFocus={handleEditorFocus}
              onBlur={handleEditorBlur}
              onClick={handleEditorClick}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
            />
            {editorIsEmpty && !focused && (
              <ComposerPlaceholder>{composerPlaceholder}</ComposerPlaceholder>
            )}
          </ComposerInputWrap>

          <InputRow ref={rowRef}>
            <InputRowLeft ref={leftRef}>
              {/* + 메뉴 */}
              <FixedAction>
                <Tooltip label={t('tooltip.menu')} position="top">
                  <IconBtn aria-label={t('tooltip.menu')} onClick={handlePlusClick} disabled={isBusy}>
                    <Plus size={18} />
                  </IconBtn>
                </Tooltip>
              </FixedAction>

              {/* 3개 슬롯(access / summary / expanded)은 항상 마운트해 두고 현재
                * tier 에 맞지 않는 것만 레이아웃 밖으로 치운다(visibility:hidden).
                * 숨은 슬롯도 자연 폭을 잴 수 있어야 다른 tier 로 되돌아갈지 판단이
                * 되고, 라벨이 바뀌면 ResizeObserver 가 재측정을 부른다. */}
              <ComposerSlotBox {...{ [COMPOSER_SLOT_ATTR]: 'access' }} $hidden={composerTier === 'narrow'}>
                <McpApprovalSelect
                  disabled={isBusy}
                  open={openComposerMenu === 'approval'}
                  onOpenChange={(open) => setOpenComposerMenu(open ? 'approval' : null)}
                />
              </ComposerSlotBox>

              <ComposerSlotBox {...{ [COMPOSER_SLOT_ATTR]: 'summary' }} $hidden={composerTier === 'full'}>
                <CompactComposerSettings
                  model={chatModel}
                  modelOptions={chatModelOptions}
                  onModelChange={setChatModel}
                  showModel={showChatModelPicker}
                  showReasoning={reasoningSupported}
                  interactive={
                    interactiveSupported
                      ? { enabled: interactiveMode, onToggle: toggleInteractiveMode }
                      : null
                  }
                  mode={effectiveReboMode}
                  modeOptions={reboModeOptions}
                  onModeChange={(mode) => {
                    setReboMode(mode);
                    if (mode === 'skill') openSkillModal();
                  }}
                  reasoningEnabled={reasoningEnabled}
                  onToggleReasoning={toggleReasoning}
                  access={composerTier === 'narrow'}
                  busy={isBusy}
                />
              </ComposerSlotBox>

              <ExpandedSettings {...{ [COMPOSER_SLOT_ATTR]: 'expanded' }} $hidden={composerTier !== 'full'}>
                {/* 모델 선택기 — 통합/문서 에이전트 입력창, Rebo 모드 왼쪽 */}
                {showChatModelPicker && (
                  <ComposerSelect
                    title="Model"
                    value={chatModel}
                    options={chatModelOptions}
                    onChange={setChatModel}
                    open={openComposerMenu === 'model'}
                    onOpenChange={(open) => setOpenComposerMenu(open ? 'model' : null)}
                    fallbackLabel="gemma-4"
                  />
                )}

                {/* Rebo 모드 선택기 (live-agent 모드에서는 Fast 고정, 드롭다운 숨김) */}
                {resolvedWorkspaceMode === 'live-agent' ? (
                  <ReboModeArea>
                    <ReboModeLabel style={{ padding: '4px 8px', opacity: 0.7 }}>
                      {t('reboMode.fastLabel')}
                    </ReboModeLabel>
                  </ReboModeArea>
                ) : (
                  <ComposerSelect
                    title="Rebo"
                    value={effectiveReboMode}
                    options={reboModeOptions}
                    onChange={(mode) => {
                      setReboMode(mode);
                      if (mode === 'skill') openSkillModal();
                    }}
                    open={openComposerMenu === 'mode'}
                    onOpenChange={(open) => setOpenComposerMenu(open ? 'mode' : null)}
                    fallbackLabel="Auto"
                  />
                )}

                {/* Interactive(HITL) 토글 — on 일 때만 에이전트가 되묻는다 */}
                {interactiveSupported && (
                  <Tooltip
                    label={interactiveMode ? t('interactive.tooltipOn') : t('interactive.tooltipOff')}
                    position="top"
                  >
                    <InteractiveToggle
                      type="button"
                      role="switch"
                      aria-checked={interactiveMode}
                      $on={interactiveMode}
                      onClick={toggleInteractiveMode}
                      disabled={isBusy}
                    >
                      <InteractiveToggleTrack $on={interactiveMode}>
                        <InteractiveToggleKnob $on={interactiveMode} />
                      </InteractiveToggleTrack>
                      <ReboModeLabel>{t('interactive.label')}</ReboModeLabel>
                    </InteractiveToggle>
                  </Tooltip>
                )}

                {/* Reasoning 토글 — Interactive 오른쪽에서 이번 턴의 추론을 제어 */}
                {reasoningSupported && (
                  <Tooltip
                    label={reasoningEnabled ? t('reasoning.tooltipOn') : t('reasoning.tooltipOff')}
                    position="top"
                  >
                    <InteractiveToggle
                      type="button"
                      role="switch"
                      aria-checked={reasoningEnabled}
                      $on={reasoningEnabled}
                      onClick={toggleReasoning}
                      disabled={isBusy}
                    >
                      <InteractiveToggleTrack $on={reasoningEnabled}>
                        <InteractiveToggleKnob $on={reasoningEnabled} />
                      </InteractiveToggleTrack>
                      <ReboModeLabel>{t('reasoning.label')}</ReboModeLabel>
                    </InteractiveToggle>
                  </Tooltip>
                )}

              </ExpandedSettings>
            </InputRowLeft>

            {/* 전송 / 중지 / 업로드 중 */}
            <FixedAction>
              {isUploadingConversation ? (
                <Tooltip label={t('chat.fileUploading')}>
                  <IconBtn $primary $disabled aria-label={t('chat.fileUploading')}>
                    <SpinIcon><Loader size={18} /></SpinIcon>
                  </IconBtn>
                </Tooltip>
              ) : isStreaming ? (
                <Tooltip label={t('tooltip.stopResponse')}>
                  <IconBtn $primary aria-label={t('tooltip.stopResponse')} onClick={() => stopStreaming(runtimeConversationId)}>
                    <Square size={16} fill="currentColor" />
                  </IconBtn>
                </Tooltip>
              ) : (
                <Tooltip label={t('tooltip.send')}>
                  <IconBtn $primary aria-label={t('tooltip.send')} $disabled={!canSend} onClick={handleSubmit} disabled={!canSend}>
                    <ArrowUp size={18} />
                  </IconBtn>
                </Tooltip>
              )}
            </FixedAction>
          </InputRow>
        </InputCard>

      </Wrapper>

      {mentionMenu.open && filteredMentionSuggestions.length > 0 && (
        <MentionDropdown style={{ left: mentionMenu.x, top: mentionMenu.y }}>
          {filteredMentionSuggestions.map((mention, idx) => (
            <MentionSuggestionRow
              key={mention.id}
              $focused={idx === mentionMenu.activeIndex}
              onMouseEnter={() => {
                setMentionMenu((prev) => ({ ...prev, activeIndex: idx }));
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMentionChip(mention);
              }}
            >
              <MentionSuggestionType>
                {mention.kind === 'agent' ? 'Agent' : t('chat.teamMember')}
              </MentionSuggestionType>
              <MentionSuggestionLabel>{mention.display}</MentionSuggestionLabel>
            </MentionSuggestionRow>
          ))}
        </MentionDropdown>
      )}

      {/* + 드롭다운 메뉴 */}
      <DropdownMenu
        open={plusMenu.open}
        x={plusMenu.x}
        y={plusMenu.y}
        direction="up"
        align="left"
        onClose={() => setPlusMenu((prev) => ({ ...prev, open: false }))}
        items={plusMenuItemIds ? plusMenuItems.filter((item) => plusMenuItemIds.includes(item.id)) : plusMenuItems}
        constrainToViewport
      />

      <WorkspaceFilesModal
        open={workspaceFilesModal.open}
        onClose={() =>
          setWorkspaceFilesModal((prev) => ({ ...prev, open: false }))
        }
        agentId={workspaceFilesModal.agentId}
        mode={workspaceFilesModal.mode}
        agentLabel={workspaceFilesModal.agentLabel}
        onAttach={appendAttachmentEntries}
      />

      {WikiSourcePicker && (
        <Suspense fallback={null}>
          <WikiSourcePicker
            open={wikiPickerOpen}
            onClose={() => setWikiPickerOpen(false)}
            current={projectWikiBinding}
            onBind={handleBindProjectWiki}
          />
        </Suspense>
      )}

      {/* 붙여넣은 텍스트 뷰어 모달 */}
      <Modal
        open={!!viewingBlock}
        onClose={() => setViewingBlock(null)}
        title={t('chat.pastedContent')}
        titleIcon={<Clipboard size={16} />}
        width="580px"
      >
        {viewingBlock && (
          <PastedModalContent>
            <PastedModalTextarea
              value={viewingBlock.content}
              onChange={(e) => {
                const next = { ...viewingBlock, content: e.target.value };
                setViewingBlock(next);
                setPastedBlocks((prev) =>
                  prev.map((b) => (b.id === viewingBlock.id ? next : b)),
                );
              }}
              autoFocus
            />
            <PastedModalFooter>
              <PastedModalConfirmBtn onClick={() => setViewingBlock(null)}>
                {t('button.confirm')}
              </PastedModalConfirmBtn>
            </PastedModalFooter>
          </PastedModalContent>
        )}
      </Modal>

      {/* 스킬 관리 모달 */}
      {systemPromptSupported && (
        <SystemPromptModal
          open={systemPromptModalOpen}
          onClose={() => setSystemPromptModalOpen(false)}
          mode={resolvedWorkspaceMode}
          centerPaddingLeft={modalCenterPadding.left}
          centerPaddingRight={modalCenterPadding.right}
        />
      )}
      <SkillModal
        open={skillModalOpen}
        onClose={closeSkillModal}
        initialTab={skillModalInitialTab}
        centerPaddingLeft={modalCenterPadding.left}
        centerPaddingRight={modalCenterPadding.right}
      />

    </OuterWrapper>
  );
};

/* ---- Rebo Mode Selector ---- */

const InputRowLeft = styled.div`
  position: relative; /* 접힌 ExpandedSettings 의 absolute 기준 */
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 4px;
`;

const FixedAction = styled.div`
  display: inline-flex;
  flex-shrink: 0;
`;

/* 현재 tier 에 속하지 않는 슬롯을 레이아웃 밖으로 치우는 공통 규칙.
 * display:none 은 폭이 0 이 되어 "다른 tier 로 돌아가도 되는지" 판단이
 * 불가능하다. visibility:hidden 은 포커스·접근성 트리에서도 빠지므로 숨김으로
 * 충분하고, absolute 라 행 높이에도 영향이 없다. right:0 으로 왼쪽으로
 * 삐져나가게 두는 이유: LTR 에서 왼쪽(음수 x) 오버플로는 스크롤 영역에
 * 포함되지 않아, 숨은 블록이 모바일 화면에 가로 스크롤을 만들지 않는다. */
const parkedSlot = css`
  position: absolute;
  right: 0;
  top: 0;
  width: max-content;
  visibility: hidden;
  pointer-events: none;
`;

const ComposerSlotBox = styled.div<{ $hidden: boolean }>`
  display: flex;
  align-items: center;
  min-width: 0;
  ${({ $hidden }) => $hidden && parkedSlot}
`;

const ExpandedSettings = styled.div<{ $hidden: boolean }>`
  display: flex;
  align-items: center;
  /* 한 줄 고정. 폭이 모자라면 줄바꿈 대신 useComposerTier 가 요약 버튼으로
   * 접는다 — 중첩 wrap 이 만들던 3~5줄 계단 레이아웃(모바일·태블릿)이 원인. */
  flex-wrap: nowrap;
  min-width: 0;
  gap: 4px;
  ${({ $hidden }) => $hidden && parkedSlot}
`;

const ReboModeArea = styled.div`
  position: relative;
`;

const ReboModeLabel = styled.span`
  line-height: 1;
`;

/* ---- Interactive (HITL) toggle ---- */

const InteractiveToggle = styled.button<{ $on: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme, $on }) =>
    $on ? theme.colors.textPrimary : theme.colors.textMuted};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

const InteractiveToggleTrack = styled.span<{ $on: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 26px;
  height: 15px;
  padding: 2px;
  border-radius: 999px;
  background: ${({ theme, $on }) =>
    $on
      ? theme.colors.brand
      : theme.mode === 'dark'
        ? 'rgba(255,255,255,0.16)'
        : 'rgba(0,0,0,0.14)'};
  transition: background ${({ theme }) => theme.transitions.fast};
`;

const InteractiveToggleKnob = styled.span<{ $on: boolean }>`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transform: translateX(${({ $on }) => ($on ? '11px' : '0')});
  transition: transform 0.16s ease;
`;

/* ---- HITL question card slot ---- */

const HitlCardSlot = styled.div`
  margin-bottom: 8px;
`;
